import {
  Recipe,
  RecipeCategory,
  RecipeIngredientResponse,
  PaginatedRecipeResponse,
} from "@/types/recipe";
import { getEnvironmentConfig } from "@/config/environment";
import { mockRecipeService } from "../mock/recipes";

export class RecipeApiService {
  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async getRecipes(businessUnitId: number, offset: number = 0, limit: number = 20): Promise<PaginatedRecipeResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/v1/recipes/business-unit/${businessUnitId}?limit=${limit}&offset=${offset}`,
      {
        headers: {
          'ngrok-skip-browser-warning': '1'
        }
      }
    );
    const data = await response.json();
    console.log("API Response:", data);
    return {
      recipes: data.recipes.map(recipe => ({
        ...recipe,
        description: recipe.description === 0 ? "" : recipe.description,
        preparationSteps: [],
        history: [],
        ingredients: []
      })),
      pagination: {
        total: data.pagination.total,
        count: data.pagination.count,
        page: data.pagination.page,
        pages: data.pagination.pages,
        limit: data.pagination.limit,
        offset: data.pagination.offset
      }
    };
  }

  async getRecipeById(id: string): Promise<Recipe | undefined> {
    const response = await fetch(`${this.baseUrl}/recipes/${id}`, {
      headers: {
        'ngrok-skip-browser-warning': '1'
      }
    });
    const data = await response.json();
    return data ? {
      ...data,
      description: data.description === 0 ? "" : data.description,
      preparationSteps: data.preparationSteps || [],
      history: data.history || []
    } : undefined;
  }

  async getRecipeWithIngredients(
    recipeId: number,
    businessUnitId: number
  ): Promise<RecipeIngredientResponse> {
    console.log('Fetching recipe with ingredients:', { recipeId, businessUnitId });
    const response = await fetch(
      `${this.baseUrl}/api/v1/recipes/business-unit/${businessUnitId}/with-ingredients?recipe_id=${recipeId}`,
      {
        headers: {
          'ngrok-skip-browser-warning': '1'
        }
      }
    );
    const data = await response.json();
    console.log('Recipe with ingredients API response:', data);
    return data;
  }

  async getRecipesByCategory(category: RecipeCategory): Promise<Recipe[]> {
    const response = await fetch(
      `${this.baseUrl}/recipes?category=${category}`,
      {
        headers: {
          'ngrok-skip-browser-warning': '1'
        }
      }
    );
    const data = await response.json();
    return data.map(recipe => ({
      ...recipe,
      description: recipe.description === 0 ? "" : recipe.description,
      preparationSteps: recipe.preparationSteps || [],
      history: recipe.history || [],
      ingredients: recipe.ingredients || []
    }));
  }

  async updateRecipe(recipe: Recipe): Promise<Recipe> {
    const response = await fetch(`${this.baseUrl}/recipes/${recipe.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        'ngrok-skip-browser-warning': '1'
      },
      body: JSON.stringify(recipe),
    });
    return response.json();
  }
}

// Factory function to get the appropriate service based on environment
export const getRecipeService = () => {
  const config = getEnvironmentConfig();

  if (config.useMockData) {
    return mockRecipeService;
  }

  return new RecipeApiService(config.apiUrl);
};

// Export a singleton instance
export const recipeService = getRecipeService();
